//----------------------------------------------------------------------------------------------------------------------

const float FLOAT_COMPARISON_EPSILON = 0.0001; // Epsilon for floating-point comparisons
const int MAX_ALLOWED_CATEGORICAL_VALUES = 7; // Maximum number of categorical values that can be stored

// Coords along the component's surface from its bottom-left (xy) and top-right (zw)
in vec4 v_component_coords;

//----------------------------------------------------------------------------------------------------------------------

// The width and height to wrap around, or -1.0 if wrapping should not occur
uniform vec2 u_wrap_dimensions;

// Anything outside of this range will show up as desaturated
uniform vec2 u_filtered_value_range;

// Array of allowed discrete values for categorical filtering
uniform float u_allowed_categorical_values[MAX_ALLOWED_CATEGORICAL_VALUES];
uniform int u_num_categorical_values_stored;

// The current color scale to apply data to
uniform sampler2D u_color_scale;

// The range of values represented by this color scale
uniform vec2 u_color_scale_range;

// The color used for data outside of the current selection
uniform vec4 u_non_selected_color;

// The CDF data texture, where R and G channels represent encode values
uniform sampler2D u_cdf_tex;

// The component coordinates of the CDF texture (x_start, y_start, x_end, y_end)
uniform vec4 u_cdf_tex_rect;

// The clipping rect (for screenshot mode)
uniform vec4 u_clip_rect;

//----------------------------------------------------------------------------------------------------------------------

/**
 * A modulo function whose return value is always positive
 * @param n                 The number to mod
 * @param m                 The number to mod by
 * @return                  An always-positive modulo of n by m
 */
float euclidean_modulo(float n, float m) {
    return m <= 0.0 ? n : mod(mod(n, m) + m, m);
}

/**
 * Determines if a value is within the selected range based on filtering mode
 * @param value             The value to check
 * @return                  True if the value should be displayed, false otherwise
 */
bool isValueInSelectedRange(float value) {
    if (u_num_categorical_values_stored > 0) {
        // Categorical filtering mode - check if value matches any allowed category
        for (int i = 0; i < u_num_categorical_values_stored; i++) {
            if (abs(value - u_allowed_categorical_values[i]) < FLOAT_COMPARISON_EPSILON) {
                return true;
            }
        }
        return false;
    } else if (u_num_categorical_values_stored == 0) {
        // No categories selected - show nothing
        return false;
    } else {
        // Range filtering mode - check if value is within min/max range
        bool allow_all_values = u_filtered_value_range[1] < u_filtered_value_range[0];
        bool allow_no_values = u_color_scale_range[1] < u_color_scale_range[0];
        return allow_all_values ||
               (!allow_no_values && value >= u_filtered_value_range[0] && value <= u_filtered_value_range[1]);
    }
}

void main() {
    // Handle clipping for screenshot mode
    if (v_component_coords.x < u_clip_rect.x) discard;
    if (v_component_coords.y < u_clip_rect.y) discard;
    if (v_component_coords.x > u_clip_rect.z) discard;
    if (v_component_coords.y > u_clip_rect.w) discard;

    vec2 coords = v_component_coords.xy - u_cdf_tex_rect.xy;
    coords.x = euclidean_modulo(coords.x, u_wrap_dimensions.x);
    coords.y = euclidean_modulo(coords.y, u_wrap_dimensions.y);
    vec2 uv = coords.xy / (u_cdf_tex_rect.zw - u_cdf_tex_rect.xy);

    // If this sample is off the surface of the CDF, then discard
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
        discard;
    }

    // If the value is NaN, then discard
    float value = texture(u_cdf_tex, uv).r;
    if (isnan(value)) {
        discard;
    }

    bool value_in_selected_range = isValueInSelectedRange(value);

    // Figure out if this bin is currently selected
    float value_acceptability = value_in_selected_range ? 1.0 : 0.0;

    // Use the desaturated color if data's unavailable, or the value is out of range... otherwise, use the colormap
    float color_in_value_range = saturate((value - u_color_scale_range[0]) / (u_color_scale_range[1] - u_color_scale_range[0]));
    vec3 selected_color = texture2D(u_color_scale, vec2(color_in_value_range, 0.0)).rgb;
    vec3 non_selected_color = vec3(u_non_selected_color.rgb + vec3(0.25, 0.25, 0.25)) * (0.25 + color_in_value_range);
    vec3 color = mix(non_selected_color, selected_color, value_acceptability);
    gl_FragColor = vec4(color, u_non_selected_color.a);
}
